import { useEffect, useState, useRef, useCallback } from "react";
import { getReport, GetAuth } from "./Verbs";
import { useVsiAppContext } from "../stores/VsiApp";
import { useNavigate, useSearchParams } from "react-router-dom";
import { putReportInCache, clearOldReports, getReportFromCache} from "./idb";
import { indexedDbMaxAgeMs } from "./utils";

const updateTitle = (title) => {
  document.title = title.replace(/\//ig, "-");
};

export function useTitleFromComponent(title) {
  useEffect(() => {
    updateTitle(title);
  }, [title]);
}

export function useEnvColor() {
  const [envColor, setEnvColor] = useState("");

  const { getAccessTokenSilently } = useVsiAppContext();

  useEffect(() => {
    if (envColor === "") {
      const getEnvColor = async () => {
        try {
          const token = await getAccessTokenSilently();
          const colorRes = await GetAuth("/api/v6/login/env_color", token);
          const color = await colorRes.data;
          setEnvColor(color);
        } catch (err) {
          console.error(err);
        }
      };

      getEnvColor();
    }
  }, [envColor, getAccessTokenSilently]);

  return envColor;
};

// params:
// route: required api uri including all query string parameters. any time the route changes, we refetch
// bouncer: optional boolean flag to have the initial fetch wait until that flag is true. this is helpful when relying on an async parameter

export function useDataWithRoute(route, bouncer) {
  const { getAccessTokenSilently, doPeriodsMatchReportContext } = useVsiAppContext();

  const [error, setError] = useState(null);
  const [dataState, setDataState] = useState({
    recordset: [],
    recordsets: [[]],
    isLoading: true
  });

  const { isLoading } = dataState;
  const controllerRef = useRef(null);

  const fetchData = useCallback(async (controller, cachedEntry) => {
    try {
      setDataState(
        prev => ({
          recordset: prev.recordset,
          recordsets: prev.recordsets,
          isLoading: !cachedEntry
        })
      );
      const token = await getAccessTokenSilently();
      const fetched = await getReport(
        route,
        { signal: controller.signal },
        token
      );

      const data = {
        recordset: fetched?.data?.recordset ?? fetched?.data ?? [],
        recordsets: fetched?.data?.recordsets ?? [fetched?.data] ?? [],
        isLoading: false
      };

      if (!cachedEntry) {
        setDataState(data);
      }

      putReportInCache(route, data).catch(console.error);

      // leaving this for now, it has to do with comparing the cache to the new results
      // if (cachedEntry) {
      //   const prevData = cachedEntry.data;
      //   const same =
      //     JSON.stringify(prevData.recordsets) === JSON.stringify(data.recordsets);
      //
      //   if (!same) {
      //     setDataState(data);
      //   }
      // }

    } catch (error) {
      console.error(error);
      setDataState(
        prev => ({
          recordset: prev.recordset,
          recordsets: prev.recordsets,
          isLoading: false
        })
      );
      setError(error);
    }
  }, [route, getAccessTokenSilently]);

  const reFetchData = useCallback(() => {
    // manually handling abort controller which is why there is duped code
    // a
    const controller = new AbortController();
    controllerRef.current = controller;
    fetchData(controller);
  }, [fetchData]);

  useEffect(() => {
    if (!doPeriodsMatchReportContext) return;
    if (route && (bouncer === undefined || bouncer)) {
      // b
      const controller = new AbortController();
      controllerRef.current = controller;

      let cachedEntry = null;

      getReportFromCache(route)
      . then((cached) => {
        const fresh = cached && Date.now() - cached.ts <= indexedDbMaxAgeMs;
        if (fresh) {
          cachedEntry = cached;
          setDataState({ ...cached.data, isLoading: false })
        }
      })
        .catch(console.error)
        .finally(() => fetchData(controller, cachedEntry));

      return () => {
        controller.abort();
      };
    }
  }, [route, bouncer, getAccessTokenSilently, fetchData, doPeriodsMatchReportContext]);

  // this had to do with maintaining the cache across tabs
  // need to come back to it
  // useEffect(() => {
  //   if (typeof BroadcastChannel !== 'undefined') {
  //     const bc = new BroadcastChannel('reports-cache');
  //     bc.onmessage = (event) => {
  //       if (event?.data?.route === route) {
  //         setDataState({ ...event.data.entry.data, isLoading: false });
  //       }
  //     };
  //     return () => bc.close();
  //   }
  // }, [route]);

  useEffect(() => {
    return () => {
      // ensures even manual re-fetch calls are aborted on unmount (using reFetchData)
      controllerRef.current?.abort();
    };
  }, []);


  return [dataState, { isLoading: dataState.isLoading, error, setData: setDataState, setError, reFetchData, clearOldReports: () => clearOldReports(indexedDbMaxAgeMs) }];
}


// This is the same as useDataWithRoute, except without stripping the whitespace from the route
// Reference: https://dev.azure.com/vsi-devops/Forward%20Unto%20Dawn/_workitems/edit/4246
export function useSwitchData(route, bouncer) {
  const { getAccessTokenSilently } = useVsiAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    recordset: [],
    recordsets: [[]]
  });

  const fetchData = useCallback(async (controller) => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const fetched = await getReport(
        route,
        { signal: controller.signal },
        token
      );
      setData(fetched?.data || []);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError(error);
    }
  }, [route, getAccessTokenSilently]);

  useEffect(() => {
    if (route && (bouncer === undefined || bouncer)) {
      const controller = new AbortController();

      fetchData(controller);
      return () => {
        controller.abort();
      };
    }
  }, [route, bouncer, fetchData, getAccessTokenSilently]);


  return [data, { isLoading, error, setData, setError, setIsLoading }];
};


export function useAuthWithRoute(route, bouncer) {
  const { getAccessTokenSilently } = useVsiAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (route) => {
    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const fetched = await GetAuth(route?.replace(/\s+/g, ""), token);
      setData(fetched?.data || []);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError(error);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (bouncer === undefined || bouncer) {
      const controller = new AbortController();

      fetchData(route);
      return () => {
        controller.abort();
      };
    }
  }, [route, bouncer, fetchData]);

  const reFetchData = useCallback(() => {
    fetchData(route.replace(/\s+/g, ""));
  }, [route, fetchData]);

  return [data, { isLoading, error, fetchData, reFetchData, setData, setError }];
}

export function useDebouncedApiCall(apiCall, delay) {
  const [isApiLoading, setIsApiLoading] = useState(false);

  let timeoutId;

  function debounceApiCall(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      setIsApiLoading(true);

      apiCall(...args)?.finally(() => {
        setIsApiLoading(false);
      });
    }, delay);
  }

  return [debounceApiCall, isApiLoading];
}

export function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedFunction = useCallback((...args) => {
    // Clear the previous timeout if the function is called again before delay.
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Set up a new timeout to call the callback after the specified delay.
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  return debouncedFunction;
}

export function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Return a cleanup function that will be called every time ...
      // ... useEffect is re-called. useEffect will only be re-called ...
      // ... if value changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is ...
      // ... changed within the delay period. Timeout gets cleared and restarted.
      // To put it in context, if the user is typing within our app's ...
      // ... search box, we don't want the debouncedValue to update until ...
      // ... they've stopped typing for more than 500ms.
      return () => {
        clearTimeout(handler);
      };
    },
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay]
  );

  return debouncedValue;
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);


  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);

}

export function useUrlState(defaultState = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // initialize the state with URL search params or default state if they don't exist
  const initialState = Object.keys(defaultState).reduce((state, key) => {
    if (Array.isArray(defaultState[key])) {
      // For arrays, grab all instances of the key from the URL.
      const values = searchParams.getAll(key);
      // values of array can't be the same... sorry
      state[key] = values.length ? Array.from(new Set(values)) : defaultState[key];
    } else {
      // For primitive values, use get.
      const paramValue = searchParams.get(key);
      state[key] = paramValue !== null ? paramValue : defaultState[key];
    }
    return state;
  }, {});

  const [state, setState] = useState(initialState);

  // update URL search params when state changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.keys(state).forEach((key) => {
      const value = state[key];
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          // Append each array element as a separate query param.
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== "") {
              newSearchParams.append(key, item);
            }
          });
        } else {
          newSearchParams.set(key, value);
        }
      } else {
        newSearchParams.delete(key);
      }
    });
    // setSearchParams(searchParams);
    // Navigate without triggering a page reload
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  }, [state, navigate]);

  // A method to update state and keep it in sync with URL search params
  const setUrlState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  return [state, setUrlState];
}
